// i18n area 'monitoring' (M5b, 2026-08-04): the Monitoring page — the
// composite health overview cards, the alert table with its ack action, the
// alert rule / alert sink management dialogs and the cluster-capacity trend
// chart. Sink secrets NEVER appear here: the keystore is write-only (M2g
// doctrine) — these strings only ever name references, never material.

export const en = {
  monitoring: {
    nav: 'Monitoring',
    title: 'Monitoring',
    loadError: 'Could not load the monitoring data — is the TVStor backend reachable?',

    // REFUI-1 (dettatura Luca 2026-08-06, "più ordine"): the one long page
    // becomes tabs — the ClusterTabs deep-link idiom (PATH routes since
    // ROUTE-1 #186).
    tabs: {
      overview: 'Overview',
      alerts: 'Alerts',
      rules: 'Rules',
      sinks: 'Sinks',
      trends: 'Trends'
    },

    // The fixed target-kind vocabulary (contract monitoring.yaml — 7 kinds
    // since OBJ-1 and SEC-TSA, #181 T12).
    kinds: {
      node: 'Node',
      cephcluster: 'Ceph cluster',
      multiscsicluster: 'MultiSCSI cluster',
      volume: 'Volume',
      share: 'Share',
      store: 'Store',
      objectstore: 'Object store',
      backuptarget: 'Backup target'
    },

    // The fixed severity ladder — a label for humans, no behavior (M5b fence).
    severities: {
      info: 'Info',
      warning: 'Warning',
      critical: 'Critical'
    },

    // The fixed metric catalog (contract monitoring.yaml, closed enum) —
    // nested by kind so t(`monitoring.metrics.${metric}`) resolves the dotted
    // metric name directly.
    metrics: {
      node: {
        cpuLoad1: 'CPU load (1 min)',
        memUsedPercent: 'Memory used',
        fsUsedPercent: 'System filesystem used',
        netRxBytesPerSec: 'Network receive rate',
        netTxBytesPerSec: 'Network transmit rate',
        netErrors: 'Network errors (interval)',
        netDrops: 'Network drops (interval)',
        replicaTunnelPeersDown: 'Replica-tunnel peers down',
        configReplicaAgeSeconds: 'Config replica age',
        crlAgeSeconds: 'Revocation list (CRL) age',
        machineAccountDivergedSeconds: 'Machine account diverged for',
        daemonRestarts: 'Daemon restart streak',
        cephDaemonsMute: 'Ceph daemons alive but mute',
        auditWriteFailures: 'Audit rows failed to write (interval)',
        replicationSpoolPercent: 'DR receive spool fill (of ceiling)',
        sanPathsDown: 'SAN paths down (external LUNs)',
        diskWearMaxPercent: 'Disk wear, worst drive (% used)',
        diskMediaErrors: 'Disk media errors (sum)',

        // ENEA-A-R16 (2026-10-24): the ONE alert a node in maintenance takes.
        maintenanceHours: 'Hours in maintenance'
      },

      // REMODEL R1 (#214, 2026-08-26): the datapool slice split into the
      // two cluster kinds — each leaf under the kind that produces it.
      cephcluster: {
        capacityGiB: 'Cluster capacity',
        usedGiB: 'Cluster space used',
        usedPercent: 'Cluster used percent',
        daysToFull: 'Days to full (forecast)',
        osdsDown: 'Ceph OSDs down',
        pgsInactive: 'Ceph PGs inactive (I/O blocked)'
      },
      multiscsicluster: {
        capacityGiB: 'Cluster capacity',
        usedGiB: 'Cluster space used',
        usedPercent: 'Cluster used percent',
        daysToFull: 'Days to full (forecast)',
        missingPvs: 'Missing physical volumes (disks)',
        scrubMismatches: 'Scrub mismatches (last pass)',
        scrubAgeDays: 'Days since last completed scrub'
      },
      volume: {
        health: 'Volume health',
        backupAgeHours: 'Last completed backup age',
        replicaLagSeconds: 'Replication lag',
        resyncPercent: 'Resync progress',
        syncDegraded: 'Sync degraded',
        readBytesPerSec: 'Read throughput',
        writeBytesPerSec: 'Write throughput',
        readIops: 'Read IOPS',
        writeIops: 'Write IOPS',
        ioAwaitMs: 'I/O latency (await)'
      },
      share: {
        health: 'Share health',
        clients: 'Connected clients',
        fsUsedPercent: 'Share filesystem used',
        daysToFull: 'Days to full (forecast)',
        daemonsNotServing: 'Daemons alive but not serving',
        daemonRestarts: 'Share daemon restart streak',
        smbLeaseUnrenewedSeconds: 'SMB lease unrenewed for',

        // ENEA-A-R12 (2026-10-23): from the client audit — only while the
        // share's audit is on (no leaf = no sample, not «zero»).
        opsPerSec: 'Client operations per second (audit)',
        bytesPerSec: 'Client bytes per second (audit)',
        deleteOps: 'Deletes in the interval (audit)',
        authFailures: 'Failed connections/authentications (audit)',
        accessDenied: 'Access denied in the interval (audit)'
      },
      store: {
        health: 'Store health',
        lagSeconds: 'Store replication lag'
      },
      objectstore: {
        health: 'Object store health',

        // ENEA-A-R12 (2026-10-23): from the S3 client audit (ops log / access log).
        requestsPerSec: 'S3 requests per second (audit)',
        bytesPerSec: 'S3 bytes per second (audit)',
        deleteOps: 'S3 deletes in the interval (audit)',
        authFailures: 'S3 authentication failures (audit)',
        accessDenied: 'S3 requests denied (audit)'
      },
      backuptarget: {
        timestampUnreachable: 'Timestamp authority unreachable (newest backup)',
        timestampRefused: 'Timestamp refused by authority (newest backup)',
        timestampUnverifiable: 'Timestamp not verifiable here (newest backup)',
        timestampInvalid: 'Backups whose timestamp no longer verifies',
        timestampTampered: 'Backups whose stamped bytes changed (TAMPER)',
        timestampAgeDays: 'Age of newest timestamp-ok backup'
      }
    },

    overview: {
      observedAt: 'Freshest observation: {time}',
      neverObserved: 'No observation sweep has completed yet',
      nodes: {
        title: 'Nodes',
        member: 'member',
        joining: 'joining'
      },
      store: {
        title: 'Store',
        health: {
          healthy: 'Healthy',
          degraded: 'Degraded',
          unavailable: 'Unavailable'
        }
      },
      storageClusters: {
        title: 'Storage clusters',
        degraded: '{count} degraded',
        allHealthy: 'all healthy'
      },
      volumes: {
        title: 'Volumes',
        ready: 'ready',
        degraded: 'degraded',
        error: 'error'
      },
      fileShares: {
        title: 'File Shares',
        degraded: '{count} degraded',
        allHealthy: 'all healthy'
      },
      alerts: {
        title: 'Alerts',
        firing: 'firing',
        unacknowledged: '{count} unacknowledged',
        none: 'No firing alerts'
      }
    },

    alerts: {
      title: 'Alerts',
      rule: 'Rule',
      target: 'Target',
      metric: 'Metric',
      severity: 'Severity',
      value: 'Value',
      threshold: 'Threshold',
      phaseLabel: 'Phase',
      phases: {
        firing: 'Firing',
        resolved: 'Resolved'
      },
      firedAt: 'Fired at',
      resolvedAt: 'Resolved at',
      storeTarget: 'Store (cluster)',
      ack: 'Acknowledge',
      ackedBy: 'Acknowledged by',
      notAcked: 'Not acknowledged',
      ackedShort: 'Ack',
      ackHint: 'Bookkeeping for the team — it silences nothing and resolves nothing',

      // MON-DELIVERY (ratifica 2026-08-12): the notification that was not
      // born is read off the episode — an absence is not a refusal.
      deliveries: 'Deliveries',
      deliveriesNone: 'No sinks configured at the transition',
      deliveriesBorn: '{expected} delivery task(s) born',
      deliveriesUnbornChip: '{unborn} of {expected} REFUSED',
      deliveriesDetail: '{expected} expected, {unborn} refused',

      // #164: the emergency surface and its restricted close.
      unclosableChip: 'UNCLOSABLE',
      unclosableHint:
        'Since {since} the system declares it cannot close this episode by itself: the evaluation window holds neither a sample nor an absence declaration. The operator close exists exactly here.',
      unclosableSince: 'Declared unclosable since',
      unclosableNone: 'Not declared — the evaluator can still close this episode by itself',
      resolveAction: 'Resolve the episode the system cannot close',
      resolveTitle: 'Resolve {name}?',
      resolveBody:
        'The system has declared since {since} that nothing it can observe will ever bring this episode down (no sample, no absence declaration reaches its evaluation window). This close does NOT fix the missing series — remove the real defect, or the next episode will end up here again.',
      resolveDeliveriesNote:
        'Resolve deliveries are born exactly as for any other close: a watched mailbox learns the episode ended, and by whom.',
      resolveDone: 'Episode resolved at {at} — the server’s row, not a client deduction.',
      resolveConfirm: 'Resolve episode'
    },

    rules: {
      title: 'Alert Rules',
      createTitle: 'Create alert rule',
      editTitle: 'Edit rule {name}',
      renameHint: 'Renaming is cosmetic — alerts reference the rule by id',
      frozenCopiesHint: 'Existing alerts keep their frozen copies; the NEXT sweep evaluates with the new values',
      name: 'Name',
      nameHint: 'Lowercase letters, digits and dashes (DNS-1123)',
      metric: 'Metric',
      metricHint: 'Metric and target kind are fixed at creation — retyping is a new rule',
      targetKind: 'Target kind',
      targetSelector: 'Target id (optional)',
      selectorHint: 'Watch ONE target by id — empty watches every target of the kind, including future ones',
      storeSelectorHint: 'The store is a singleton — no target id applies',
      operator: 'Operator',
      operators: {
        gt: 'Greater than',
        lt: 'Lower than',

        // ENEA-A-R12 (2026-10-23, §4): the deviation from the baseline.
        anomaly: 'Deviation from the baseline'
      },
      anomalyThreshold: 'Deviations from the baseline (k)',
      anomalyClearMargin: 'Hysteresis (deviations)',
      anomalyHint:
        'Deviation from the baseline: a robust EWMA over the last hours of samples (Settings → anomaly baseline), no machine learning. The alert states the value, the baseline ± σ and how many deviations; k must be > 0 (3 is the factory choice) and the hysteresis in [0, k).',
      anomalyInvalid: 'k must be > 0 and the hysteresis in [0, k)',
      anomalyCondition: 'baseline ± {k} σ',
      threshold: 'Threshold',
      forMinutes: 'Minimum duration (minutes)',
      forMinutesHint:
        'The breach must hold this long before firing — a multiple of the 5-minute sampling cadence (0 fires on the first breaching sample)',
      severity: 'Severity',
      clearMargin: 'Clear margin (hysteresis)',
      clearMarginHint:
        'A firing alert resolves only past threshold minus/plus this margin — 0 resolves at the threshold itself',
      enabled: 'Enabled',
      enabledHint: 'A disabled rule stops firing and its burning alerts resolve on the next sweep',
      disabledChip: 'Disabled',
      firingChip: '{count} firing',
      deleteWarning:
        'Firing alerts of this rule are resolved on the next sweep; resolved history keeps its episodes until retention reaps them.',

      // The collapsed secondary section of the Rules tab (REFUI-1).
      disabledTitle: 'Disabled rules',

      // #181 ondata 4: the real PATCH landed — the «CLI-only, from the
      // console it is delete-and-recreate» fence has fallen.
      disabledHint:
        'Rules kept but not evaluated — a disabled rule stops firing on the next sweep. Open a rule and edit it to re-enable it, or to change threshold, severity, duration or hysteresis.'
    },

    sinks: {
      title: 'Alert Sinks',
      createTitle: 'Create alert sink',
      editTitle: 'Edit sink {name}',
      editApplyHint:
        'Deliveries resolve the sink’s config at task run time: a change applies from the NEXT transition — in-flight deliveries finish on the config they started with.',
      credentialClearHint: 'A credential of kind smtpAccount — «No authentication» removes the reference on save',
      secretClearHint: 'A credential of kind webhookSecret — «Unsigned» removes the reference on save',
      sudoNotice: 'Sink changes are sudo-protected: a sink names where alert traffic goes.',
      name: 'Name',
      nameHint: 'Lowercase letters, digits and dashes (DNS-1123)',
      kind: 'Kind',
      kinds: {
        email: 'Email (SMTP)',
        webhook: 'Webhook (HTTPS)',
        syslog: 'Syslog (RFC 5424 over TLS)'
      },
      kindImmutableHint: 'Fixed at creation — retyping is a new sink',

      // ENEA-A-R11 (2026-10-23): the webhook's payload format and the syslog
      // sink (one row, two consumers: alert transitions by the worker,
      // journal and daemon logs by the node's rsyslog forwarder).
      format: 'Payload format',
      formats: {
        alert: 'Alert (classic payload)',
        callhome: 'Call-home (structured event, tvstor.callhome/1)'
      },
      formatHint:
        'Call-home sends a CallHomeEvent (type, severity, resource, phrase, dedupKey) with X-TVStor-Event / X-TVStor-Delivery / X-TVStor-Cluster headers — same HMAC signature, same retries',
      syslog: {
        host: 'Receiver host',
        hostRequired: 'The receiver host is required',
        port: 'Port',
        portInvalid: 'Port 1–65535 (6514 is the RFC 5425 default)',
        transport: 'Transport',
        transports: {
          tls: 'TLS (RFC 5425) — verified by CA or by pin',
          tcp: 'TCP in the clear — closed lab networks only'
        },
        fingerprint: 'Receiver certificate pin (SHA-256)',
        fingerprintHint:
          '64 hex digits, colons allowed (openssl x509 -fingerprint -sha256) — the way to trust a self-signed receiver',
        fingerprintInvalid: 'The pin is the SHA-256 fingerprint of the receiver certificate: 64 hex digits',
        caPem: 'CA certificate (PEM)',
        caPemHint: 'The CA that signs the receiver certificate; with both CA and pin, both must match',
        unverifiable:
          'With TLS either the CA or the pin is required — a receiver the sink cannot verify gets nothing (the server refuses it: syslog_sink_unverifiable)',
        serverName: 'TLS server name',
        serverNameHint: 'When the certificate name differs from the host (CA mode); empty = the host',
        clientCertificate: 'Client certificate (mTLS, keystore)',
        clientCertificateNone: 'No client certificate',
        clientCertificateHint:
          'A credential of kind tlsCertificate — the private key stays write-only in the keystore; the node renders it 0600 for rsyslog',
        facility: 'Facility',
        appName: 'APP-NAME',
        appNameInvalid: 'APP-NAME: 1–48 printable ASCII characters, no spaces',
        forward: 'What rides this sink',
        forwardAlerts: 'Alert transitions (sent by the worker, one RFC 5424 message each)',
        forwardJournal: 'The node journal (systemd-journald → rsyslog forwarder)',
        forwardDaemonLogs: 'The product daemons’ file logs (samba, ganesha, kea, postgres, s3)',
        forwarderHint:
          'Journal and daemon logs are shipped by a second rsyslogd instance the worker renders and supervises on EVERY node (packages rsyslog + rsyslog-gnutls). Secrets never ride: the product’s logs are redacted at the source.'
      },
      destination: 'Destination',
      host: 'SMTP host',
      port: 'Port',
      tls: 'Transport security',
      tlsOptions: {
        starttls: 'STARTTLS (port 587)',
        implicit: 'Implicit TLS (port 465)',
        none: 'None — closed lab networks only'
      },
      from: 'From address',
      to: 'Recipients',
      toHint: 'One address per line — every transition mail goes to all of them',
      credential: 'SMTP account (keystore)',
      credentialNone: 'No authentication',
      credentialHint: 'A credential of kind smtpAccount — the password stays write-only in the keystore, never re-read',
      url: 'Webhook URL',
      urlHint: 'https:// only — alert events carry infrastructure detail',
      secret: 'Signing secret (keystore)',
      secretNone: 'Unsigned',
      secretHint: 'A credential of kind webhookSecret — deliveries carry an HMAC-SHA256 signature header',
      enabled: 'Enabled',
      enabledHint: 'A disabled sink receives no deliveries; transitions are not queued for later',
      disabledChip: 'Disabled',
      test: 'Send test notification',
      testStarted: 'Test delivery task started — its outcome and log are the diagnosis',
      testFollow: 'open the delivery task',
      deleteTitle: 'Delete {name}?',
      deleteWarning: 'Pending deliveries to this sink fail terminally; alert evaluation continues unaffected.',

      // The collapsed secondary section of the Sinks tab (REFUI-1): disabled
      // sinks — typically kept for testing (configure → test → enable).
      // #181 ondata 4: the sink PATCH landed — the edit dialog is the enable
      // switch.
      disabledTitle: 'Disabled sinks',
      disabledHint:
        'Sinks receiving no deliveries — the configure → test → enable staging area. Open a sink and edit it (sudo) to enable it or change its destination.'
    },

    trends: {
      // #181 ondata 4: the card covers the WHOLE catalog — 7 kinds, 39
      // metrics — not only the datapool slice.
      title: 'Trends',
      targetKind: 'Target kind',
      target: 'Target',
      metric: 'Metric',
      range: 'Range',
      ranges: {
        '1h': 'Last hour',
        '6h': 'Last 6 hours',
        '24h': 'Last 24 hours',
        '7d': 'Last 7 days',
        '30d': 'Last 30 days',
        '90d': 'Last 90 days'
      },
      empty: 'No samples in the selected range — samples appear with the 5-minute observation cadence',
      noTargets: 'No targets of this kind yet — the chart appears with the first one',
      storeSingleton: 'The store is a singleton: one series per cluster, no target to pick',
      avg: 'avg',
      min: 'min',
      max: 'max',
      samples: 'samples'
    }
  }
}

export const it: typeof en = {
  monitoring: {
    nav: 'Monitoraggio',
    title: 'Monitoraggio',
    loadError: 'Impossibile caricare i dati di monitoraggio — il backend TVStor è raggiungibile?',
    tabs: {
      overview: 'Panoramica',
      alerts: 'Alert',
      rules: 'Regole',
      sinks: 'Destinazioni',
      trends: 'Trend'
    },
    kinds: {
      node: 'Nodo',
      cephcluster: 'Cluster Ceph',
      multiscsicluster: 'Cluster MultiSCSI',
      volume: 'Volume',
      share: 'Condivisione',
      store: 'Store',
      objectstore: 'Object store',
      backuptarget: 'Destinazione backup'
    },
    severities: {
      info: 'Info',
      warning: 'Avviso',
      critical: 'Critico'
    },
    metrics: {
      node: {
        cpuLoad1: 'Carico CPU (1 min)',
        memUsedPercent: 'Memoria usata',
        fsUsedPercent: 'Filesystem di sistema usato',
        netRxBytesPerSec: 'Velocità di ricezione rete',
        netTxBytesPerSec: 'Velocità di trasmissione rete',
        netErrors: 'Errori di rete (intervallo)',
        netDrops: 'Pacchetti scartati (intervallo)',
        replicaTunnelPeersDown: 'Peer del tunnel di replica giù',
        configReplicaAgeSeconds: 'Età della replica di configurazione',
        crlAgeSeconds: 'Età della lista di revoca (CRL)',
        machineAccountDivergedSeconds: 'Machine account divergente da',
        daemonRestarts: 'Serie di riavvii dei demoni',
        cephDaemonsMute: 'Demoni ceph vivi ma muti',
        auditWriteFailures: 'Righe di audit non scritte (intervallo)',
        replicationSpoolPercent: 'Riempimento spool receive DR (sul tetto)',
        sanPathsDown: 'Path SAN giù (LUN esterne)',
        diskWearMaxPercent: 'Usura dischi, il peggiore (% consumata)',
        diskMediaErrors: 'Errori media dei dischi (somma)',

        // ENEA-A-R16 (2026-10-24): l'UNICO allarme che un nodo in manutenzione prende.
        maintenanceHours: 'Ore in manutenzione'
      },

      // REMODEL R1 (#214, 2026-08-26): la fetta datapool sdoppiata nelle due
      // specie di cluster — ogni foglia sotto la specie che la produce.
      cephcluster: {
        capacityGiB: 'Capacità del cluster',
        usedGiB: 'Spazio del cluster usato',
        usedPercent: 'Percentuale del cluster usata',
        daysToFull: 'Giorni al riempimento (previsione)',
        osdsDown: 'OSD Ceph giù',
        pgsInactive: 'PG Ceph non attivi (I/O bloccato)'
      },
      multiscsicluster: {
        capacityGiB: 'Capacità del cluster',
        usedGiB: 'Spazio del cluster usato',
        usedPercent: 'Percentuale del cluster usata',
        daysToFull: 'Giorni al riempimento (previsione)',
        missingPvs: 'Volumi fisici (dischi) mancanti',
        scrubMismatches: 'Mismatch dello scrub (ultima passata)',
        scrubAgeDays: "Giorni dall'ultimo scrub completato"
      },
      volume: {
        health: 'Salute volume',
        backupAgeHours: 'Età ultimo backup completato',
        replicaLagSeconds: 'Ritardo di replica',
        resyncPercent: 'Avanzamento resync',
        syncDegraded: 'Sync degradato',
        readBytesPerSec: 'Velocità di lettura',
        writeBytesPerSec: 'Velocità di scrittura',
        readIops: 'IOPS in lettura',
        writeIops: 'IOPS in scrittura',
        ioAwaitMs: 'Latenza I/O (await)'
      },
      share: {
        health: 'Salute condivisione',
        clients: 'Client connessi',
        fsUsedPercent: 'Filesystem condivisione usato',
        daysToFull: 'Giorni al riempimento (previsione)',
        daemonsNotServing: 'Demoni vivi ma che non servono',
        daemonRestarts: 'Serie di riavvii dei demoni della condivisione',
        smbLeaseUnrenewedSeconds: 'Lease SMB non rinnovato da',
        opsPerSec: 'Operazioni client al secondo (audit)',
        bytesPerSec: 'Byte client al secondo (audit)',
        deleteOps: 'Cancellazioni nell’intervallo (audit)',
        authFailures: 'Connessioni/autenticazioni fallite (audit)',
        accessDenied: 'Accessi negati nell’intervallo (audit)'
      },
      store: {
        health: 'Salute store',
        lagSeconds: 'Ritardo di replica dello store'
      },
      objectstore: {
        health: 'Salute object store',
        requestsPerSec: 'Richieste S3 al secondo (audit)',
        bytesPerSec: 'Byte S3 al secondo (audit)',
        deleteOps: 'Cancellazioni S3 nell’intervallo (audit)',
        authFailures: 'Autenticazioni S3 fallite (audit)',
        accessDenied: 'Richieste S3 negate (audit)'
      },
      backuptarget: {
        timestampUnreachable: 'Autorità di marcatura non raggiungibile (ultimo backup)',
        timestampRefused: "Marca rifiutata dall'autorità (ultimo backup)",
        timestampUnverifiable: 'Marca non verificabile qui (ultimo backup)',
        timestampInvalid: 'Backup con marca che non si verifica più',
        timestampTampered: 'Backup con byte marcati cambiati (MANOMISSIONE)',
        timestampAgeDays: 'Età del backup più recente con marca ok'
      }
    },
    overview: {
      observedAt: 'Osservazione più recente: {time}',
      neverObserved: 'Nessun giro di osservazione ancora completato',
      nodes: {
        title: 'Nodi',
        member: 'membri',
        joining: 'in ingresso'
      },
      store: {
        title: 'Store',
        health: {
          healthy: 'Sano',
          degraded: 'Degradato',
          unavailable: 'Non disponibile'
        }
      },
      storageClusters: {
        title: 'Cluster di storage',
        degraded: '{count} degradati',
        allHealthy: 'tutti sani'
      },
      volumes: {
        title: 'Volumi',
        ready: 'pronti',
        degraded: 'degradati',
        error: 'in errore'
      },
      fileShares: {
        title: 'Condivisioni file',
        degraded: '{count} degradate',
        allHealthy: 'tutte sane'
      },
      alerts: {
        title: 'Alert',
        firing: 'attivi',
        unacknowledged: '{count} non presi in carico',
        none: 'Nessun alert attivo'
      }
    },
    alerts: {
      title: 'Alert',
      rule: 'Regola',
      target: 'Target',
      metric: 'Metrica',
      severity: 'Severità',
      value: 'Valore',
      threshold: 'Soglia',
      phaseLabel: 'Fase',
      phases: {
        firing: 'Attivo',
        resolved: 'Risolto'
      },
      firedAt: 'Scattato il',
      resolvedAt: 'Risolto il',
      storeTarget: 'Store (cluster)',
      ack: 'Prendi in carico',
      ackedBy: 'Preso in carico da',
      notAcked: 'Non preso in carico',
      ackedShort: 'Ack',
      ackHint: 'Annotazione per il team — non silenzia e non risolve nulla',
      deliveries: 'Consegne',
      deliveriesNone: 'Nessuna destinazione configurata alla transizione',
      deliveriesBorn: '{expected} task di consegna nati',
      deliveriesUnbornChip: '{unborn} su {expected} RIFIUTATE',
      deliveriesDetail: '{expected} attese, {unborn} rifiutate',
      unclosableChip: 'NON CHIUDIBILE',
      unclosableHint:
        'Dal {since} il sistema dichiara di non poter chiudere questo episodio da sé: la finestra di valutazione non contiene né un campione né una dichiarazione di assenza. La chiusura da operatore esiste esattamente qui.',
      unclosableSince: 'Dichiarato non chiudibile dal',
      unclosableNone: 'Non dichiarato — il valutatore può ancora chiudere questo episodio da sé',
      resolveAction: "Risolvi l'episodio che il sistema non può chiudere",
      resolveTitle: 'Risolvere {name}?',
      resolveBody:
        'Il sistema dichiara dal {since} che niente di osservabile potrà mai chiudere questo episodio (nessun campione, nessuna dichiarazione di assenza raggiunge la sua finestra di valutazione). Questa chiusura NON ripara la serie mancante: rimuovi il difetto vero, o il prossimo episodio finirà di nuovo qui.',
      resolveDeliveriesNote:
        "Le consegne di risoluzione nascono esattamente come per ogni altra chiusura: una casella sorvegliata apprende che l'episodio è finito, e per mano di chi.",
      resolveDone: 'Episodio risolto il {at} — la riga del server, non una deduzione del client.',
      resolveConfirm: 'Risolvi episodio'
    },
    rules: {
      title: 'Regole di alert',
      createTitle: 'Crea regola di alert',
      editTitle: 'Modifica regola {name}',
      renameHint: 'Rinominare è cosmetico — gli alert riferiscono la regola per id',
      frozenCopiesHint: 'Gli alert esistenti conservano le copie congelate; il PROSSIMO giro valuta coi valori nuovi',
      name: 'Nome',
      nameHint: 'Minuscole, cifre e trattini (DNS-1123)',
      metric: 'Metrica',
      metricHint: 'Metrica e tipo di target sono fissati alla creazione — cambiarli è una nuova regola',
      targetKind: 'Tipo di target',
      targetSelector: 'Id del target (opzionale)',
      selectorHint: 'Sorveglia UN target per id — vuoto sorveglia ogni target del tipo, anche futuri',
      storeSelectorHint: 'Lo store è un singleton — nessun id di target si applica',
      operator: 'Operatore',
      operators: {
        gt: 'Maggiore di',
        lt: 'Minore di',
        anomaly: 'Scostamento dalla baseline'
      },
      anomalyThreshold: 'Deviazioni dalla baseline (k)',
      anomalyClearMargin: 'Isteresi (deviazioni)',
      anomalyHint:
        'Scostamento dalla baseline: una EWMA robusta sulle ultime ore di campioni (Impostazioni → baseline delle anomalie), niente machine learning. L’allarme dice il valore, la baseline ± σ e quante deviazioni; k deve essere > 0 (3 è la scelta di fabbrica) e l’isteresi in [0, k).',
      anomalyInvalid: 'k deve essere > 0 e l’isteresi in [0, k)',
      anomalyCondition: 'baseline ± {k} σ',
      threshold: 'Soglia',
      forMinutes: 'Durata minima (minuti)',
      forMinutesHint:
        'La violazione deve durare almeno così tanto prima di scattare — un multiplo della cadenza di campionamento di 5 minuti (0 scatta al primo campione oltre soglia)',
      severity: 'Severità',
      clearMargin: 'Margine di rientro (isteresi)',
      clearMarginHint:
        'Un alert attivo si risolve solo oltre soglia meno/più questo margine — 0 risolve alla soglia stessa',
      enabled: 'Abilitata',
      enabledHint: 'Una regola disabilitata smette di scattare e i suoi alert attivi si risolvono al giro successivo',
      disabledChip: 'Disabilitata',
      firingChip: '{count} attivi',
      deleteWarning:
        'Gli alert attivi di questa regola vengono risolti al giro successivo; lo storico risolto conserva gli episodi finché la retention non li rimuove.',
      disabledTitle: 'Regole disabilitate',
      disabledHint:
        'Regole conservate ma non valutate — una regola disabilitata smette di scattare al giro successivo. Apri una regola e modificala per riabilitarla, o per cambiare soglia, severità, durata o isteresi.'
    },
    sinks: {
      title: 'Destinazioni di alert',
      createTitle: 'Crea destinazione di alert',
      editTitle: 'Modifica destinazione {name}',
      editApplyHint:
        "Le consegne risolvono la configurazione della destinazione al momento dell'esecuzione del task: una modifica si applica dalla PROSSIMA transizione — le consegne in volo finiscono con la configurazione con cui sono partite.",
      credentialClearHint:
        'Una credenziale di tipo smtpAccount — «Nessuna autenticazione» rimuove il riferimento al salvataggio',
      secretClearHint: 'Una credenziale di tipo webhookSecret — «Non firmato» rimuove il riferimento al salvataggio',
      sudoNotice:
        'Le modifiche alle destinazioni sono protette da sudo: una destinazione decide dove va il traffico di alert.',
      name: 'Nome',
      nameHint: 'Minuscole, cifre e trattini (DNS-1123)',
      kind: 'Tipo',
      kinds: {
        email: 'Email (SMTP)',
        webhook: 'Webhook (HTTPS)',
        syslog: 'Syslog (RFC 5424 su TLS)'
      },
      kindImmutableHint: 'Fissato alla creazione — cambiarlo è una nuova destinazione',
      format: 'Formato del payload',
      formats: {
        alert: 'Alert (payload classico)',
        callhome: 'Call-home (evento strutturato, tvstor.callhome/1)'
      },
      formatHint:
        'Call-home manda un CallHomeEvent (type, severity, resource, phrase, dedupKey) con gli header X-TVStor-Event / X-TVStor-Delivery / X-TVStor-Cluster — stessa firma HMAC, stessi retry',
      syslog: {
        host: 'Host del ricevitore',
        hostRequired: "L'host del ricevitore è obbligatorio",
        port: 'Porta',
        portInvalid: 'Porta 1–65535 (6514 è il default RFC 5425)',
        transport: 'Trasporto',
        transports: {
          tls: 'TLS (RFC 5425) — verificato con la CA o col pin',
          tcp: 'TCP in chiaro — solo reti di laboratorio chiuse'
        },
        fingerprint: 'Pin del certificato del ricevitore (SHA-256)',
        fingerprintHint:
          '64 cifre esadecimali, due punti ammessi (openssl x509 -fingerprint -sha256) — il modo di fidarsi di un ricevitore self-signed',
        fingerprintInvalid: "Il pin è l'impronta SHA-256 del certificato del ricevitore: 64 cifre esadecimali",
        caPem: 'Certificato della CA (PEM)',
        caPemHint: 'La CA che firma il certificato del ricevitore; con CA e pin insieme, devono valere entrambi',
        unverifiable:
          'Con TLS serve la CA o il pin — un ricevitore che la destinazione non può verificare non riceve nulla (il server lo rifiuta: syslog_sink_unverifiable)',
        serverName: 'Nome TLS del server',
        serverNameHint: "Quando il nome nel certificato differisce dall'host (modo CA); vuoto = l'host",
        clientCertificate: 'Certificato client (mTLS, keystore)',
        clientCertificateNone: 'Nessun certificato client',
        clientCertificateHint:
          'Una credenziale di tipo tlsCertificate — la chiave privata resta write-only nel keystore; il nodo la rende 0600 per rsyslog',
        facility: 'Facility',
        appName: 'APP-NAME',
        appNameInvalid: 'APP-NAME: da 1 a 48 caratteri ASCII stampabili, senza spazi',
        forward: 'Cosa viaggia su questa destinazione',
        forwardAlerts: 'Le transizioni di allarme (le manda il worker, un messaggio RFC 5424 ciascuna)',
        forwardJournal: 'Il giornale del nodo (systemd-journald → forwarder rsyslog)',
        forwardDaemonLogs: 'I log a file dei demoni del prodotto (samba, ganesha, kea, postgres, s3)',
        forwarderHint:
          'Giornale e log dei demoni li spedisce una seconda istanza di rsyslogd che il worker rende e supervisiona su OGNI nodo (pacchetti rsyslog + rsyslog-gnutls). I segreti non viaggiano: i log del prodotto sono redatti alla sorgente.'
      },
      destination: 'Destinazione',
      host: 'Host SMTP',
      port: 'Porta',
      tls: 'Sicurezza del trasporto',
      tlsOptions: {
        starttls: 'STARTTLS (porta 587)',
        implicit: 'TLS implicito (porta 465)',
        none: 'Nessuna — solo reti di laboratorio chiuse'
      },
      from: 'Indirizzo mittente',
      to: 'Destinatari',
      toHint: 'Un indirizzo per riga — ogni transizione va a tutti',
      credential: 'Account SMTP (keystore)',
      credentialNone: 'Nessuna autenticazione',
      credentialHint: 'Una credenziale di tipo smtpAccount — la password resta write-only nel keystore, mai riletta',
      url: 'URL del webhook',
      urlHint: 'Solo https:// — gli eventi di alert contengono dettagli infrastrutturali',
      secret: 'Segreto di firma (keystore)',
      secretNone: 'Non firmato',
      secretHint: 'Una credenziale di tipo webhookSecret — le consegne portano una firma HMAC-SHA256',
      enabled: 'Abilitata',
      enabledHint: 'Una destinazione disabilitata non riceve consegne; le transizioni non vengono accodate per dopo',
      disabledChip: 'Disabilitata',
      test: 'Invia notifica di prova',
      testStarted: 'Task di consegna di prova avviato — esito e log del task sono la diagnosi',
      testFollow: 'apri il task di consegna',
      deleteTitle: 'Eliminare {name}?',
      deleteWarning:
        'Le consegne pendenti verso questa destinazione falliscono definitivamente; la valutazione degli alert prosegue.',
      disabledTitle: 'Destinazioni disabilitate',
      disabledHint:
        "Destinazioni senza consegne — l'area di allestimento configura → prova → abilita. Apri una destinazione e modificala (sudo) per abilitarla o cambiarne la destinazione."
    },
    trends: {
      title: 'Trend',
      targetKind: 'Tipo di target',
      target: 'Target',
      metric: 'Metrica',
      range: 'Intervallo',
      ranges: {
        '1h': 'Ultima ora',
        '6h': 'Ultime 6 ore',
        '24h': 'Ultime 24 ore',
        '7d': 'Ultimi 7 giorni',
        '30d': 'Ultimi 30 giorni',
        '90d': 'Ultimi 90 giorni'
      },
      empty:
        "Nessun campione nell'intervallo selezionato — i campioni compaiono con la cadenza di osservazione di 5 minuti",
      noTargets: 'Ancora nessun target di questo tipo — il grafico compare col primo',
      storeSingleton: 'Lo store è un singleton: una serie per cluster, nessun target da scegliere',
      avg: 'media',
      min: 'min',
      max: 'max',
      samples: 'campioni'
    }
  }
}
